import React from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';

// Enhanced Table Components following AG Grid principles and data table best practices
const TableContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  margin-top: 1rem;
  font-family: inherit; /* Use the site's default font family */
  font-variant-numeric: tabular-nums; /* Use tabular numerals for better alignment */
`;

const TableHeader = styled.div`
  display: grid;
  background: #f8fafc;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableRow = styled.div`
  display: grid;
  border-bottom: 1px solid #f3f4f6;
  min-height: 48px; /* Regular row height as per best practices */
  
  &:nth-of-type(even) {
    background: #f9fafb; /* Zebra striping for better readability */
  }
  
  &:hover {
    background: #f3f4f6;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const HeaderCell = styled.div`
  padding: 0.75rem 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  
  /* First column gets more left padding for better visual balance */
  &:first-of-type {
    padding-left: 1rem;
  }
  
  /* Dynamic text alignment based on column type */
  text-align: ${props => {
    if (props.align === 'right') return 'right';
    if (props.align === 'center') return 'center';
    return 'left';
  }};
  justify-content: ${props => {
    if (props.align === 'right') return 'flex-end';
    if (props.align === 'center') return 'center';
    return 'flex-start';
  }};
`;

const DataCell = styled.div`
  padding: 0.75rem 0.5rem;
  color: #374151;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  line-height: 1.4;
  
  /* First column gets more left padding for better visual balance */
  &:first-of-type {
    padding-left: 1rem;
  }
  
  /* Dynamic text alignment based on column type */
  text-align: ${props => {
    if (props.align === 'right') return 'right';
    if (props.align === 'center') return 'center';
    return 'left';
  }};
  justify-content: ${props => {
    if (props.align === 'right') return 'flex-end';
    if (props.align === 'center') return 'center';
    return 'flex-start';
  }};
  
  /* Numeric styling for better readability */
  ${props => props.isNumeric && `
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum';
  `}
`;

// Helper function to determine column alignment based on data type
const getColumnAlignment = (column) => {
  if (column.align) return column.align;
  if (column.type === 'number' || column.type === 'currency' || column.type === 'percentage') return 'right';
  if (column.type === 'date' || column.type === 'datetime') return 'left';
  if (column.type === 'status' || column.type === 'boolean') return 'center';
  return 'left'; // Default for text
};

// Helper function to determine if column contains numeric data
const isNumericColumn = (column) => {
  return ['number', 'currency', 'percentage'].includes(column.type);
};

// Enhanced DataTable Component with AG Grid-style sizing and best practices
const DataTable = ({ 
  columns = [], 
  data = [], 
  keyField = 'id',
  rowKey = 'id',
  onRowClick,
  className,
  density = 'regular', // 'condensed', 'regular', 'relaxed'
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  searchable = false,
  sortable = false,
  pagination = false,
  pageSize = 50,
  pageSizeOptions = [50, 100, 250],
  showSelectAll = false,
  onSelectAll,
  getRowStyle = null, // Function to get custom row styles
  getRowAttributes = null, // Function to get custom row attributes
  'data-cy': dataCy, // Support data-cy for testing (table container)
  rowDataCy = 'table-row' // Support data-cy for individual rows
}) => {
  const actualKeyField = rowKey || keyField;
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSize);
  // Selection handlers
  const handleSelectAll = (e) => {
    if (!selectable || !onSelectionChange) return;
    
    if (e.target.checked) {
      const allKeys = data.map(row => row[actualKeyField]);
      onSelectionChange(allKeys);
    } else {
      onSelectionChange([]);
    }
  };

  const handleRowSelect = (rowKeyValue, e) => {
    e.stopPropagation();
    if (!selectable || !onSelectionChange) return;
    
    if (selectedRows.includes(rowKeyValue)) {
      onSelectionChange(selectedRows.filter(key => key !== rowKeyValue));
    } else {
      onSelectionChange([...selectedRows, rowKeyValue]);
    }
  };

  // Handle "Select All" button (all records, not just current page)
  const handleSelectAllRecords = () => {
    if (onSelectAll) {
      onSelectAll();
    } else if (onSelectionChange) {
      const allKeys = data.map(row => row[actualKeyField]);
      onSelectionChange(allKeys);
    }
  };

  const isAllSelected = selectable && data.length > 0 && 
    data.every(row => selectedRows.includes(row[actualKeyField]));

  // Pagination logic
  const totalPages = pagination ? Math.ceil(data.length / currentPageSize) : 1;
  const paginatedData = pagination 
    ? data.slice((currentPage - 1) * currentPageSize, currentPage * currentPageSize)
    : data;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setCurrentPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  // Calculate grid template columns with AG Grid-style sizing
  const selectColumn = selectable ? '50px ' : '';
  const gridTemplateColumns = selectColumn + columns.map(column => {
    if (column.width) return column.width;
    if (column.type === 'number' || column.type === 'currency') return 'minmax(100px, 150px)';
    if (column.type === 'date' || column.type === 'datetime') return 'minmax(120px, 180px)';
    if (column.type === 'status' || column.type === 'boolean') return 'minmax(80px, 120px)';
    return 'minmax(150px, 1fr)'; // Default flexible width
  }).join(' ');

  // Dynamic row height based on density
  const rowHeight = {
    condensed: '40px',
    regular: '48px',
    relaxed: '56px'
  }[density];

  return (
    <>
    <Global
      styles={css`
        /* Checkbox styling with black background when checked */
        input[type="checkbox"] {
          appearance: none;
          width: 16px;
          height: 16px;
          border: 2px solid #d1d5db;
          border-radius: 3px;
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
        }
        
        input[type="checkbox"]:hover {
          border-color: #9ca3af;
        }
        
        input[type="checkbox"]:checked {
          background: #1a1a1a;
          border-color: #1a1a1a;
        }
        
        input[type="checkbox"]:checked::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 14px;
          font-weight: 900;
          line-height: 1;
        }
      `}
    />
    <TableContainer className={className} data-cy={dataCy}>
      <TableHeader style={{ gridTemplateColumns }}>
        {selectable && (
          <HeaderCell align="center" style={{ minHeight: rowHeight }}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              style={{ cursor: 'pointer' }}
            />
          </HeaderCell>
        )}
        {columns.map((column, index) => (
          <HeaderCell 
            key={index} 
            align={getColumnAlignment(column)}
            style={{ 
              ...column.headerStyle,
              minHeight: rowHeight
            }}
            data-cy={`column-${column.field || column.header?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {column.header}
          </HeaderCell>
        ))}
      </TableHeader>
      
      {paginatedData.map((row, rowIndex) => (
        <TableRow 
          key={row[actualKeyField] || rowIndex}
          data-cy={rowDataCy}
          onClick={() => onRowClick?.(row)}
          style={{ 
            cursor: onRowClick ? 'pointer' : 'default',
            gridTemplateColumns,
            minHeight: rowHeight,
            ...(getRowStyle ? getRowStyle(row, rowIndex) : {})
          }}
          {...(getRowAttributes ? getRowAttributes(row, rowIndex) : {})}
        >
          {selectable && (
            <DataCell align="center" style={{ minHeight: rowHeight }}>
              <input
                type="checkbox"
                checked={selectedRows.includes(row[actualKeyField])}
                onChange={(e) => handleRowSelect(row[actualKeyField], e)}
                style={{ cursor: 'pointer' }}
              />
            </DataCell>
          )}
          {columns.map((column, colIndex) => (
            <DataCell 
              key={colIndex} 
              align={getColumnAlignment(column)}
              isNumeric={isNumericColumn(column)}
              data-cy={`cell-${column.field || column.header?.toLowerCase().replace(/\s+/g, '-')}`}
              style={{ 
                ...column.cellStyle,
                minHeight: rowHeight
              }}
            >
              {column.render ? column.render(row, rowIndex) : row[column.field]}
            </DataCell>
          ))}
        </TableRow>
      ))}
    </TableContainer>
    
    {/* Pagination and controls */}
    {(pagination && totalPages > 1) || (selectable && showSelectAll) ? (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        borderTop: '1px solid #e5e7eb',
        background: 'white',
        borderRadius: '0 0 8px 8px'
      }}>
        {/* Left side - Select All button and info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {selectable && showSelectAll && (
            <button
              onClick={handleSelectAllRecords}
              style={{
                padding: '0.5rem 1rem',
                background: isAllSelected ? '#1a1a1a' : '#f3f4f6',
                color: isAllSelected ? 'white' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isAllSelected ? `All ${data.length} Selected` : `Select All ${data.length}`}
            </button>
          )}
          {pagination && (
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Showing {((currentPage - 1) * currentPageSize) + 1} to {Math.min(currentPage * currentPageSize, data.length)} of {data.length}
            </span>
          )}
        </div>

        {/* Right side - Pagination controls */}
        {pagination && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', marginRight: '0.5rem' }}>
              Per page:
            </span>
            <select
              value={currentPageSize}
              onChange={handlePageSizeChange}
              style={{
                padding: '0.375rem 0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                marginRight: '1rem'
              }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                fontSize: '0.875rem',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              First
            </button>
            
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                fontSize: '0.875rem',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            
            <span style={{ fontSize: '0.875rem', color: '#374151', padding: '0 0.5rem' }}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                fontSize: '0.875rem',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
            
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                fontSize: '0.875rem',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Last
            </button>
          </div>
        )}
      </div>
    ) : null}
    </>
  );
};

export default DataTable;